import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, Card, Heading, Stack, Text } from '../ui';
import { useAuth } from '../auth/AuthProvider';

/** Location state carried by {@link RequireAuth} so login can return the user to where they were. */
interface LoginLocationState {
  readonly from?: string;
}

/**
 * The sign-in screen.
 *
 * Registration and password reset are hosted by Entra External ID, so this page is deliberately thin:
 * "Sign in" and "Create account" both start the hosted flow (the latter on its sign-up experience),
 * and reset is a link Entra renders on that page. Once authenticated it redirects to the originally
 * requested route.
 *
 * @returns The login screen element.
 */
export function LoginPage(): React.JSX.Element {
  const { status, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LoginLocationState | null)?.from ?? '/';

  useEffect(() => {
    if (status === 'authenticated') {
      navigate(from, { replace: true });
    }
  }, [status, from, navigate]);

  return (
    <Stack align="center" gap="xl">
      <Card ariaLabel="Sign in">
        <Stack gap="lg">
          <Stack gap="xs">
            <Heading level={1}>Sign in to Greasemap</Heading>
            <Text muted>Your garage, your RSVPs, and the races you run are private to your account.</Text>
          </Stack>

          <Stack direction="row" gap="sm" wrap>
            <Button variant="primary" onClick={() => signIn()}>
              Sign in
            </Button>
            <Button onClick={() => signIn({ createAccount: true })}>Create account</Button>
          </Stack>

          <Text size="sm" muted>
            Forgot your password? Use the “Forgot password?” link on the sign-in page.
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
